import { Link } from 'react-router-dom'
import type { ArticleData, ContentBlock, ContentPageData } from '../types'

const featureHeroImage = '/assets/media/2025/12/beit2-1.webp'

const mediaCardAuthors: Record<number, string[]> = {
  907: [
    'فضيلة الدكتور / علي شمس الدين',
    'فضيلة الأستاذ الدكتور / إبراهيم الهدهد',
    'فضيلة الشيخ / إبراهيم الصوفي',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
    'فضيلة الأستاذ الدكتور / محمد مهنا',
  ],
}

export function ContentPage({ page, articles }: { page: ContentPageData; articles: ArticleData[] }) {
  switch (page.id) {
    case 1175:
      return <ListingPage page={page} articles={articles.filter((article) => article.id >= 1300).slice(0, 3)} />
    case 709:
      return <CouncilsPage page={page} />
    case 563:
      return <ImamPage page={page} />
    case 488:
    case 377:
      return <SplitInfoPage page={page} />
    case 376:
      return <MethodPage page={page} />
    case 309:
      return <BooksPage page={page} />
    case 307:
      return <DoctorPage page={page} />
    case 305:
      return <ScientificComplexPage page={page} />
    case 304:
      return <BeitPage page={page} />
    case 378:
    case 308:
      return <FeatureOnlyPage page={page} />
    case 481:
    case 226:
    case 2:
      return <PlainLegacyPage page={page} />
    default:
      return <GenericPage page={page} />
  }
}

export function ArticlePage({ article }: { article: ArticleData }) {
  if (article.id >= 1300 || (article.id >= 1160 && article.id <= 1162)) return <NewsArticlePage article={article} />
  return <MediaArticlePage article={article} />
}

function PageHero({ title, image }: { title: string; image?: string }) {
  return (
    <section className={`inner-page-hero${image ? ' inner-page-hero--photo' : ''}`} style={image ? { backgroundImage: `url("${image}")` } : undefined}>
      <h1>{title}</h1>
    </section>
  )
}

function ListingPage({ page, articles }: { page: ContentPageData; articles: ArticleData[] }) {
  const fallbackItems = toImageHeadingPairs(page.blocks)
  const isEventsPage = page.id === 1175
  return (
    <main>
      <PageHero title={page.title} />
      <section className={`route-section listing-page-section${page.id === 306 ? ' listing-page-section--issues' : ''}${isEventsPage ? ' listing-page-section--events' : ''}`}>
        <div className="route-shell route-listing-grid">
          {articles.length > 0 ? articles.map((article) => (
            <article className="route-listing-card" key={article.id}>
              <Link to={`/${article.slug}`} className="route-listing-image"><img src={article.image} alt={article.title} loading="lazy" /></Link>
              <h2><Link to={`/${article.slug}`}>{article.title}</Link></h2>
              {isEventsPage && article.excerpt && <p>{article.excerpt}</p>}
            </article>
          )) : fallbackItems.map((item, index) => (
            <article className="route-listing-card" key={`${item.title}-${index}`}>
              <div className="route-listing-image"><img src={item.image} alt={item.title} loading="lazy" /></div>
              <h2>{item.title}</h2>
            </article>
          ))}
        </div>
      </section>
    </main>
  )
}

function CouncilsPage({ page }: { page: ContentPageData }) {
  const councils = toImageHeadingPairs(page.blocks)
  const councilDestinations = [
    '/تصوف-وسلوك',
    '/أصول-الدين',
    '/القرآن-الكريم-وعلومه',
    '/الحديث-الشريف-وعلومه-السيرة-النبوية-ا',
    '/الفقه-وعلومه',
    '/اللغة-العربية',
  ]
  return (
    <main>
      <PageHero title={page.title} />
      <section className="route-section councils-section">
        <div className="route-shell councils-grid">
          {councils.map((council, index) => (
            <article className="council-card" key={`${council.title}-${index}`}>
              <Link className="council-card-link" to={councilDestinations[index] ?? '/المجالس-المحمدية'}>
                <h3>{council.title}</h3>
              </Link>
            </article>
          ))}
        </div>
      </section>
    </main>
  )
}

const councilArchiveItems = [
  {
    title: 'تصوف وسلوك',
    slug: 'تصوف-وسلوك',
    excerpt: 'شرح كتاب “تاج العروس الحاوي لتهذيب النفوس” – الإمام ابن عطاء الله السكندري فضيلة الدكتور / علي شمس الدين مجلس شرح كتاب “تعليم المتعلم” – الإمام برهان الدين الزرنوجي فضيلة الأستاذ الدكتور / إبراهيم الهدهد شرح رسالة “المرجع” : معالم المشروع والممنوع من ممارسات التصوف المعاصر -الإمام إبراهيم الخليل بن علي الشاذلي فضيلة الشيخ / إبراهيم الصوفي',
  },
  {
    title: 'أصول الدين',
    slug: 'أصول-الدين',
    excerpt: 'شرح كتاب “الخريدة البهية” – الإمام الدرديري فضيلة الأستاذ الدكتور / عرفة النادي شرح كتاب “الأنوار القدسية في الفوائد الخرشية لحل ألفاظ العقيدة السنوسية” – الإمام محمد بن عبد الله الخراشي المالكي شيخ الجامع الأزهر فضيلة الأستاذ الدكتور / عرفة النادي',
  },
  {
    title: 'القرآن الكريم وعلومه',
    slug: 'القرآن-الكريم-وعلومه',
    excerpt: 'شرح كتاب “ثلاث رسائل في إعجاز القرآن” – الرماني والخطَّابي وعبدالقادر الجرجاني فضيلة الأستاذ الدكتور/ إبراهيم الهدهد شرح كتاب “ملاك التأويل القاطع بذوي الإلحاد والتعطيل” – الإمام الغرناطي فضيلة الأستاذ الدكتور/ إبراهيم الهدهد',
  },
  {
    title: 'الحديث الشريف وعلومه – السيرة النبوية الشريفة',
    slug: 'الحديث-الشريف-وعلومه-السيرة-النبوية-ا',
    excerpt: 'شرح كتاب “المدخل إلى معرفة كتاب الإكليل” – الإمام أبي عبد الله الحاكم فضيلة الدكتور/ أيمن الحجار شرح كتاب: “معرفة علوم الحديث” – الإمام الحاكم النيسابوري فضيلة الدكتور/ أيمن الحجار شرح كتاب”جواب الحافظ المنذري عن أسئلة في الجرح والتعديل” – الإمام المنذري فضيلة الدكتور/ أيمن الحجار محاضرة بعنوان “منهجية دراسة علم تخريج الحديث النبوي بين الرواية والدراية”',
  },
  {
    title: 'الفقه وأصوله',
    slug: 'الفقه-وعلومه',
    excerpt: 'فقه شافعي “شرح ابن القاسم على متن أبي شجاع” فضيلة الشيخ / عبد العزيز الشهاوي منهاج الطالبين وعمدة المفتين – الإمام النووي فضيلة الشيخ / عبد العزيز الشهاوي فتح العلام بشرح الإعلام في أحاديث الأحكام – شيخ الإسلام زكريا الأنصاري فضيلة الدكتور/ أحمد ممدوح فقه مالكي شرح متن العشماوية مع تقريرات الإمام الصفتي فضيلة الدكتور / عطية مصطفى',
  },
  {
    title: 'اللغة العربية',
    slug: 'اللغة-العربية',
    excerpt: 'شرح قطر الندى وبل الصدى – إبن هشام فضيلة الشيخ / ياسر مدين شرح قطر الندى وبل الصدى – إبن هشام فضيلة الشيخ / جعفر عبد الله شرح قطر الندى وبل الصدى – إبن هشام فضيلة الدكتور/ محمد حسن عثمان شرح كتاب “التحفة السنية شرح المقدمة الآجرومية – ابن آجروم فضيلة الأستاذ الدكتور/ فتحي حجازي',
  },
]

export function CouncilsArchivePage() {
  return (
    <main>
      <section className="category-archive route-section">
        <div className="route-shell category-archive-content">
          <h1>التصنيف: المجالس المحمدية</h1>
          {councilArchiveItems.map((item) => (
            <article key={item.slug}>
              <h2><Link to={`/${item.slug}`}>{item.title}</Link></h2>
              <p>{item.excerpt}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  )
}

function BooksPage({ page }: { page: ContentPageData }) {
  const books: { image: string; title: string; description: string[] }[] = []
  for (const block of page.blocks) {
    if (block.type === 'image') books.push({ image: block.value, title: '', description: [] })
    else if (block.type === 'heading' && books.length) books[books.length - 1].title = block.value
    else if (block.type === 'paragraph' && books.length) books[books.length - 1].description.push(block.value)
  }
  return (
    <main>
      <PageHero title={page.title} />
      <section className="route-section books-route-section">
        <div className="route-shell books-route-grid">
          {books.map((book, index) => (
            <article className="book-route-card" key={`${book.title}-${index}`}>
              <div className="book-route-cover"><img src={book.image} alt={`غلاف كتاب ${book.title}`} loading="lazy" /></div>
              <h3>{book.title}</h3>
              {book.description.map((line, lineIndex) => <p key={lineIndex}>{line}</p>)}
            </article>
          ))}
        </div>
      </section>
    </main>
  )
}

function SplitInfoPage({ page }: { page: ContentPageData }) {
  const image = page.blocks.find((block) => block.type === 'image')
  const copy = page.blocks.filter((block) => block.type === 'paragraph')
  return (
    <main>
      <PageHero title={page.title} />
      <section className={`route-section split-info-section${page.id === 377 ? ' split-info-section--academy' : ''}`}>
        <div className="route-shell split-info-grid">
          {image && <img src={image.value} alt={image.alt || page.title} />}
          <div className="split-info-copy">{copy.map((block, index) => <p key={index}>{block.value}</p>)}</div>
        </div>
      </section>
    </main>
  )
}

function DoctorPage({ page }: { page: ContentPageData }) {
  const [image, label, name, ...remaining] = page.blocks
  const paragraphs = remaining.filter((block) => block.type === 'paragraph')
  const headings = remaining.filter((block) => block.type === 'heading')
  return (
    <main className="doctor-page">
      <PageHero title={page.title} />
      <section className="route-section profile-section doctor-section">
        <div className="route-shell">
          <div className="profile-intro">
            {image?.type === 'image' && <img className="profile-main-image" src={image.value} alt={page.title} />}
            <div className="profile-copy">
              {label?.type === 'heading' && <h2 className="profile-label">{label.value}</h2>}
              {name?.type === 'heading' && <h2 className="profile-name">{name.value}</h2>}
              <div className="profile-intro-text">
                {paragraphs.slice(0, 2).map((block, index) => <p key={index}>{block.value}</p>)}
              </div>
            </div>
          </div>
          <div className="profile-wide-copy">{paragraphs.slice(2).map((block, index) => <p key={index}>{block.value}</p>)}</div>
          {headings.length > 0 && <div className="profile-quote">{headings.map((block, index) => index === 0 ? <h2 key={index}>{block.value}</h2> : <p key={index}>{block.value}</p>)}</div>}
        </div>
      </section>
    </main>
  )
}

function ImamPage({ page }: { page: ContentPageData }) {
  const b = page.blocks
  return (
    <main>
      <PageHero title={page.title} />
      <section className="route-section profile-section imam-section">
        <div className="route-shell">
          <ProfileRow image={b[0]} blocks={b.slice(1, 5)} />
          <ProfileRow image={b[7]} blocks={b.slice(5, 7)} reverse />
          <ProfileRow image={b[8]} blocks={b.slice(9, 11)} />
        </div>
        <div className="route-shell imam-conclusion">
          <h2>{b[11]?.value}</h2>
          {b.slice(12, 15).map((block, index) => block.type === 'paragraph' && <p key={index}>{block.value}</p>)}
          <p className="imam-empty-paragraph">&nbsp;</p>
          {b[15]?.type === 'image' && <div className="imam-gallery" aria-hidden="true"><img src={b[15].value} alt="" loading="lazy" /></div>}
          {b[19]?.type === 'paragraph' && <p className="imam-after-gallery">{b[19].value}</p>}
          <Link className="small-live-button" to="/كتب-الإمام-الرائد">اعرف المزيد</Link>
        </div>
      </section>
    </main>
  )
}

function ProfileRow({ image, blocks, reverse = false }: { image?: ContentBlock; blocks: ContentBlock[]; reverse?: boolean }) {
  return (
    <div className={`profile-row${reverse ? ' profile-row--reverse' : ''}${blocks.every((block) => block.type === 'paragraph') ? ' profile-row--body-only' : ''}`}>
      {image?.type === 'image' && <img src={image.value} alt={image.alt || ''} loading="lazy" />}
      <div className="profile-copy">
        {blocks.map((block, index) => {
          if (block.type === 'heading') return index === 0 ? <h2 className="profile-label" key={index}>{block.value}</h2> : <h2 className="profile-name" key={index}>{block.value}</h2>
          if (block.type === 'paragraph') return <p key={index}>{block.value}</p>
          return null
        })}
      </div>
    </div>
  )
}

function MethodPage({ page }: { page: ContentPageData }) {
  return <main><PageHero title={page.title} /><section className="route-section method-section"><div className="route-shell method-content"><BlockList blocks={page.blocks} /></div></section></main>
}

function ScientificComplexPage({ page }: { page: ContentPageData }) {
  const b = page.blocks
  const features = [
    { heading: b[2], text: b[3], image: b[4] },
    { heading: b[6], text: b[7], image: b[5] },
    { heading: b[8], text: b[9], image: b[10] },
  ]
  return (
    <main className="scientific-page">
      <section className="institution-intro scientific-intro"><div className="route-shell"><h2 className="institution-title">{b[0]?.value || page.title}</h2>{b[1]?.type === 'paragraph' && <p>{b[1].value}</p>}</div></section>
      <section className="institution-features route-shell">
        {features.map((feature, index) => (
          <article className={`institution-feature${index % 2 ? ' institution-feature--reverse' : ''}`} key={index}>
            {feature.image?.type === 'image' && <img src={feature.image.value} alt={feature.heading?.value || ''} loading="lazy" />}
            <div>
              {feature.heading?.type === 'heading' && <h2>{feature.heading.value}</h2>}
              {feature.text?.type === 'paragraph' && <p>{feature.text.value}</p>}
              <Link className="small-live-button" to={index === 0 ? '/أكاديمية-أهل-الصفة-لدراسات-التصوف' : index === 1 ? '/المجالس-المحمدية' : '/المجالس-الحديثة'}>المزيد</Link>
            </div>
          </article>
        ))}
      </section>
    </main>
  )
}

function BeitPage({ page }: { page: ContentPageData }) {
  const b = page.blocks
  const sections = [
    { image: b[18], heading: b[19], copy: b[20] },
    { image: b[23], heading: b[21], copy: b[22] },
    { image: b[24], heading: b[25], copy: b[26] },
    { image: b[29], heading: b[27], copy: b[28] },
    { image: b[30], heading: b[31], copy: b[32] },
    { image: b[35], heading: b[33], copy: b[34] },
    { image: b[36], heading: b[37], copy: b[38] },
    { image: b[41], heading: b[39], copy: b[40] },
  ]
  return (
    <main className="beit-route-page">
      <section className="institution-intro beit-intro">
        <div className="route-shell beit-intro-grid">
          <div className="beit-intro-copy"><h2 className="institution-title">{b[0]?.value || page.title}</h2>{b[1]?.type === 'paragraph' && <p>{b[1].value}</p>}</div>
          <div className="beit-details">
            <div className="beit-details-table">
              <div className="beit-detail-labels">{[2, 3, 4].map((index) => <h2 key={index}>{b[index]?.value}</h2>)}</div>
              <div className="beit-detail-values">{[5, 6, 7].map((index) => <p key={index}>{b[index]?.value}</p>)}</div>
            </div>
            {b[8]?.type === 'image' && <img src={b[8].value} alt="شعار البيت المحمدي" />}
          </div>
        </div>
      </section>
      <section className="beit-leadership route-shell">
        {[
          { heading: b[9], copy: b[10], to: '/الإمام-الرائد' },
          { heading: b[12], copy: b[13], to: '/د-محمد-مهنا' },
          { heading: b[15], copy: b[16], to: '/الطريقة-المحمدية-الشاذلية' },
        ].map((item, index) => <article key={index}><h2>{item.heading?.value}</h2><p>{item.copy?.value}</p><Link className="beit-more-link" to={item.to}><span>المزيد</span></Link></article>)}
      </section>
      <section className="institution-features beit-features route-shell">
        {sections.map((section, index) => (
          <article className={`institution-feature${index % 2 ? ' institution-feature--reverse' : ''}`} key={index}>
            {section.image?.type === 'image' && <img src={section.image.value} alt={section.heading?.value || ''} loading="lazy" />}
            <div>{section.heading?.type === 'heading' && <h2>{section.heading.value}</h2>}{section.copy?.type === 'paragraph' && <p>{section.copy.value}</p>}</div>
          </article>
        ))}
      </section>
    </main>
  )
}

function MediaArticlePage({ article }: { article: ArticleData }) {
  const groups = toMediaGroups(article.blocks)
  const authors = mediaCardAuthors[article.id] ?? []
  let mediaIndex = 0
  return (
    <main className={`media-article media-article--${article.id}`}>
      <PageHero title={article.title} image={featureHeroImage} />
      <section className="route-section media-section"><div className="route-shell media-groups">
        {groups.map((group, groupIndex) => <section className="media-group" key={`${group.title}-${groupIndex}`}>
          {group.title && <h2 className="media-group-heading">{group.title}</h2>}
          <div className="media-grid">
            {group.items.map((item, index) => {
              const author = authors[mediaIndex++]
              return <article className="media-card" key={`${item.title}-${index}`}><div className="media-video"><iframe src={item.video.replace(/&amp;/g, '&')} title={`${article.title} ${groupIndex + 1}-${index + 1}`} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div><h2>{item.title}</h2>{author && <p className="media-card-author">{author}</p>}</article>
            })}
          </div>
        </section>)}
      </div></section>
    </main>
  )
}

function NewsArticlePage({ article }: { article: ArticleData }) {
  return <main className={`news-article-page news-article-page--${article.id}${article.blocks.length === 0 ? ' news-article-page--empty' : ''}`}><article className="news-article-body"><h1>{article.title}</h1>{article.blocks.map((block, index) => block.type === 'paragraph' && <p key={index}>{block.value}</p>)}</article></main>
}

function FeatureOnlyPage({ page }: { page: ContentPageData }) {
  return <main className={`feature-only-page feature-only-page--${page.id}`}><PageHero title={page.title} image={featureHeroImage} /></main>
}

function PlainLegacyPage({ page }: { page: ContentPageData }) {
  return <main className={`plain-legacy-page plain-legacy-page--${page.id}`}><article><h1>{page.title}</h1><BlockList blocks={page.blocks} /></article></main>
}

function GenericPage({ page }: { page: ContentPageData }) {
  return <main><PageHero title={page.title} /><section className="route-section"><div className="route-shell generic-route-copy"><BlockList blocks={page.blocks} /></div></section></main>
}

function BlockList({ blocks }: { blocks: ContentBlock[] }) {
  return <>{blocks.map((block, index) => {
    if (block.type === 'heading') return <h2 key={index}>{block.value}</h2>
    if (block.type === 'paragraph') return <p key={index}>{block.value}</p>
    if (block.type === 'listItem') return <p className="list-item" key={index}>{block.value}</p>
    if (block.type === 'image') return <img className="content-image" key={index} src={block.value} alt={block.alt || ''} loading="lazy" />
    return <div className="video-frame" key={index}><iframe src={block.value.replace(/&amp;/g, '&')} title={`فيديو ${index + 1}`} loading="lazy" allowFullScreen /></div>
  })}</>
}

function toImageHeadingPairs(blocks: ContentBlock[]) {
  const pairs: { image: string; title: string }[] = []
  for (const block of blocks) {
    if (block.type === 'image') pairs.push({ image: block.value, title: '' })
    else if (block.type === 'heading' && pairs.length) pairs[pairs.length - 1].title = block.value
  }
  return pairs
}

function toMediaGroups(blocks: ContentBlock[]) {
  const groups: { title: string; items: { video: string; title: string }[] }[] = []
  let group = { title: '', items: [] as { video: string; title: string }[] }
  groups.push(group)
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index]
    if (block.type === 'video') group.items.push({ video: block.value, title: '' })
    else if (block.type === 'heading' && blocks[index - 1]?.type === 'video' && group.items.length) group.items[group.items.length - 1].title = block.value
    else if (block.type === 'heading') {
      if (!group.items.length && !group.title) group.title = block.value
      else {
        group = { title: block.value, items: [] }
        groups.push(group)
      }
    }
  }
  return groups.filter((item) => item.title || item.items.length)
}

export function NotFoundPage() {
  return <main className="not-found"><div><span>٤٠٤</span><h1>الصفحة غير موجودة</h1><p>ربما تغير عنوان الصفحة أو لم يعد متاحاً.</p><Link className="button" to="/">العودة إلى الرئيسية</Link></div></main>
}
